import React, { useState } from 'react';
import { Plus, Trash2, Star, FileText, Download } from 'lucide-react';
import StructureService from '../../../services/StructureService';
import ImageCarousel from '../ImageCarousel';
import { ConfirmationModal, toast } from '../V2Components';
import { PLACEHOLDER_IMAGES } from '../../../constants/images';
import { uploadErrorMessage } from '../../../utils/uploadErrors';
import { useFilePicker } from '../../../hooks/useFilePicker';

// UX caps — mirror api/structures.py MAX_EVENT_PHOTOS / MAX_EVENT_DOCUMENTS.
// The frontend cap disables the add button; the backend stays the source of
// truth and rejects over-limit uploads with a 400.
const MAX_PHOTOS = 10;
const MAX_DOCUMENTS = 10;

/**
 * Photo gallery + document/invoice list for one StructureEvent. Photos go
 * through ImageCarousel with set-primary/delete controls (mirrors
 * RoomImageManager); documents (invoices, warranty PDFs) render as a list of
 * download links. Files are split from the embedded `event.files` array by
 * `file_kind`.
 *
 * @param {Object} event - StructureEvent with an embedded `files` array
 * @param {Function} onChange - Called after a successful mutation so the parent refetches
 */
const StructureEventFiles = ({ event, onChange }) => {
    const [uploadingPhoto, setUploadingPhoto] = useState(false);
    const [uploadingDoc, setUploadingDoc] = useState(false);
    const [fileToDelete, setFileToDelete] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const [settingPrimaryId, setSettingPrimaryId] = useState(null);

    const files = event.files || [];
    const photos = files.filter((f) => f.file_kind === 'photo');
    const documents = files.filter((f) => f.file_kind === 'document');

    const photosFull = photos.length >= MAX_PHOTOS;
    const docsFull = documents.length >= MAX_DOCUMENTS;
    const busy = uploadingPhoto || uploadingDoc || deleting || settingPrimaryId !== null;

    const uploadFiles = async (fileList, kind, setUploading, cap, current) => {
        if (!fileList || fileList.length === 0) return;
        const remaining = cap - current;
        if (remaining <= 0) {
            toast.error(`At most ${cap} ${kind === 'photo' ? 'photos' : 'documents'} per event`);
            return;
        }
        const toUpload = Array.from(fileList).slice(0, remaining);
        if (toUpload.length < fileList.length) {
            toast.error(`Only ${remaining} more can be added`);
        }

        setUploading(true);
        let succeeded = 0;
        let lastError = null;
        try {
            for (const f of toUpload) {
                try {
                    await StructureService.uploadEventFile(event.id, f, kind);
                    succeeded += 1;
                } catch (err) {
                    lastError = err;
                }
            }
        } finally {
            // Await the parent refetch before releasing the busy state: onChange
            // is StructuresTab's async fetchStructures, and returning early hands
            // the controls back while the file counts are still stale, so the cap
            // arithmetic above runs on pre-upload numbers. (The backend re-checks
            // the cap under the event lock, so the worst case is a surprising 400
            // rather than an overshoot.) fetchStructures handles its own errors.
            if (succeeded > 0) await onChange?.();
            setUploading(false);
        }

        const failed = toUpload.length - succeeded;
        if (failed === 0) {
            toast.success(`${succeeded} file${succeeded === 1 ? '' : 's'} uploaded`);
        } else if (succeeded > 0) {
            toast.error(`Uploaded ${succeeded}, ${failed} failed: ` + uploadErrorMessage(lastError, 'upload failed'));
        } else {
            toast.error(uploadErrorMessage(lastError, 'Failed to upload'));
        }
    };

    const { triggerPicker: openPhotoPicker, fileInputElement: photoInput } = useFilePicker({
        kind: 'image',
        multiple: true,
        maxSizeMB: 25,
        onFiles: (fl) => uploadFiles(fl, 'photo', setUploadingPhoto, MAX_PHOTOS, photos.length),
        disabled: photosFull || busy,
        // Distinct testids: this component mounts the hook twice, so the default
        // 'file-picker-input' would match two inputs inside one expanded event
        // and trip Playwright's strict mode.
        testId: 'structure-photo-picker',
    });

    const { triggerPicker: openDocPicker, fileInputElement: docInput } = useFilePicker({
        kind: 'image-or-pdf',
        multiple: true,
        maxSizeMB: 25,
        onFiles: (fl) => uploadFiles(fl, 'document', setUploadingDoc, MAX_DOCUMENTS, documents.length),
        disabled: docsFull || busy,
        testId: 'structure-document-picker',
    });

    const handleSetPrimary = async (photo) => {
        if (busy) return;
        setSettingPrimaryId(photo.id);
        try {
            await StructureService.setEventFilePrimary(photo.id);
            toast.success('Primary photo updated');
            await onChange?.();
        } catch (err) {
            toast.error(err.message || 'Failed to set primary photo');
        } finally {
            setSettingPrimaryId(null);
        }
    };

    const handleDeleteConfirmed = async () => {
        if (!fileToDelete) return;
        setDeleting(true);
        try {
            await StructureService.deleteEventFile(fileToDelete.id);
            toast.success('File deleted');
            await onChange?.();
            setFileToDelete(null);
        } catch (err) {
            toast.error(err.message || 'Failed to delete file');
        } finally {
            setDeleting(false);
        }
    };

    return (
        <div className="space-y-4">
            {/* -------------------------------- Photos ------------------------------- */}
            <div>
                <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-semibold text-[var(--text-secondary)] uppercase">
                        Photos ({photos.length}/{MAX_PHOTOS})
                    </span>
                    {photoInput}
                    <button
                        type="button"
                        disabled={photosFull || busy}
                        onClick={openPhotoPicker}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-[var(--primary)] text-[var(--primary-text)] rounded-full text-xs font-bold shadow-sm hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                        aria-label="Add photo"
                    >
                        {uploadingPhoto ? (
                            <>
                                <span className="animate-spin rounded-full h-3 w-3 border-b border-current" />
                                Uploading...
                            </>
                        ) : photosFull ? (
                            'Max reached'
                        ) : (
                            <>
                                <Plus size={14} />
                                Add Photo
                            </>
                        )}
                    </button>
                </div>

                {photos.length > 0 && (
                    <>
                        <div className="flex items-center justify-center">
                            <ImageCarousel images={photos} aspectRatio="aspect-[4/3]" className="w-full max-w-lg" />
                        </div>
                        <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1 mt-2">
                            {photos.map((img) => (
                                <div
                                    key={img.id}
                                    className="relative flex-shrink-0 w-20 h-20 rounded-xl overflow-hidden border border-[var(--border)] bg-[var(--bg-card)]"
                                >
                                    <img
                                        src={img.url || PLACEHOLDER_IMAGES.house}
                                        alt={img.original_filename || 'Structure photo'}
                                        className="w-full h-full object-cover"
                                        onError={(e) => {
                                            if (e.currentTarget.src !== PLACEHOLDER_IMAGES.house) {
                                                e.currentTarget.onerror = null;
                                                e.currentTarget.src = PLACEHOLDER_IMAGES.house;
                                            }
                                        }}
                                    />
                                    {img.is_primary ? (
                                        <span
                                            className="absolute top-1 left-1 bg-[var(--primary)] text-[var(--primary-text)] rounded-full p-1 shadow"
                                            title="Primary photo"
                                        >
                                            <Star size={10} fill="currentColor" />
                                        </span>
                                    ) : (
                                        <button
                                            type="button"
                                            disabled={busy}
                                            onClick={() => handleSetPrimary(img)}
                                            title="Set as primary"
                                            aria-label="Set as primary"
                                            className="absolute top-1 left-1 bg-white/90 text-[var(--primary)] rounded-full p-1 shadow hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            {settingPrimaryId === img.id ? (
                                                <span className="block animate-spin rounded-full h-[10px] w-[10px] border-b border-current" />
                                            ) : (
                                                <Star size={10} />
                                            )}
                                        </button>
                                    )}
                                    <button
                                        type="button"
                                        disabled={busy}
                                        onClick={() => setFileToDelete(img)}
                                        title="Delete photo"
                                        aria-label="Delete photo"
                                        className="absolute top-1 right-1 bg-white/90 text-[var(--error)] rounded-full p-1 shadow hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        <Trash2 size={10} />
                                    </button>
                                </div>
                            ))}
                        </div>
                    </>
                )}
            </div>

            {/* ------------------------------ Documents ------------------------------ */}
            <div>
                <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-semibold text-[var(--text-secondary)] uppercase">
                        Invoices &amp; Documents ({documents.length}/{MAX_DOCUMENTS})
                    </span>
                    {docInput}
                    <button
                        type="button"
                        disabled={docsFull || busy}
                        onClick={openDocPicker}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-[var(--bg-card)] border border-[var(--border)] text-[var(--text-main)] rounded-full text-xs font-bold shadow-sm hover:bg-[var(--bg-hover)] disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                        aria-label="Add document"
                    >
                        {uploadingDoc ? (
                            <>
                                <span className="animate-spin rounded-full h-3 w-3 border-b border-current" />
                                Uploading...
                            </>
                        ) : docsFull ? (
                            'Max reached'
                        ) : (
                            <>
                                <Plus size={14} />
                                Add Document
                            </>
                        )}
                    </button>
                </div>

                {documents.length > 0 && (
                    <ul className="space-y-1">
                        {documents.map((doc) => (
                            <li
                                key={doc.id}
                                className="flex items-center gap-2 p-2 rounded-lg bg-[var(--bg-hover)] border border-[var(--border)]"
                            >
                                <FileText size={16} className="text-[var(--text-muted)] shrink-0" />
                                <span className="flex-1 truncate text-sm text-[var(--text-main)]">
                                    {doc.original_filename || 'Document'}
                                </span>
                                {doc.url && (
                                    <a
                                        href={doc.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="p-1.5 rounded-lg text-[var(--text-muted)] hover:text-[var(--primary)] hover:bg-[var(--bg-card)] transition"
                                        title="Open / download"
                                        aria-label="Open document"
                                    >
                                        <Download size={16} />
                                    </a>
                                )}
                                <button
                                    type="button"
                                    disabled={busy}
                                    onClick={() => setFileToDelete(doc)}
                                    title="Delete document"
                                    aria-label="Delete document"
                                    className="p-1.5 rounded-lg text-[var(--text-muted)] hover:text-[var(--error)] hover:bg-[var(--bg-card)] transition disabled:opacity-50"
                                >
                                    <Trash2 size={16} />
                                </button>
                            </li>
                        ))}
                    </ul>
                )}
            </div>

            <ConfirmationModal
                isOpen={!!fileToDelete}
                onClose={() => setFileToDelete(null)}
                onConfirm={handleDeleteConfirmed}
                isLoading={deleting}
                title="Delete this file?"
                message="Are you sure you want to delete this file? This action cannot be undone."
                confirmText={deleting ? 'Deleting...' : 'Delete'}
                isDanger
                icon={Trash2}
            />
        </div>
    );
};

export default StructureEventFiles;
